import { webRequestClient } from '@/services'
import { find, get, includes, isEmpty, isNil, size, toNumber, cloneDeep } from 'lodash'
import helpers from './helpers'
import { countChildren, errorHandler, documentLink, windowUrl } from '@/utils/helpers'
import dayjs from 'dayjs'
import uuRequestKeys from 'uuid/v1'

const actions = {
    // DATA RETRIEVAL
    retrieveHierarchyOfSelectedTaxonomy({ commit, rootState }, params) {
        commit('setIsLoading', { item: 'taxonomy', isLoading: true })
        return webRequestClient
            .get('/audience_admin/wam_custom_segments/hierarchy/json', {
                params: { product_account_id: toNumber(rootState.account.accountId), wam_custom_taxonomy_id: params.taxoId }
            })
            .then(response => {
                if (get(response, 'status', '') === 'KO' || isEmpty(response)) {
                    errorHandler('Error while retrieving your taxonomy', commit)
                } else {
                    const _response = response.root[0]
                    if (!isEmpty(_response)) {
                        commit('setSelectedTaxoId', { id: _response.id })
                        commit('setRetrievedItems', {
                            id: _response.id,
                            items: helpers.parseItems(_response.children)
                        })
                    }
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => commit('setIsLoading', { item: 'taxonomy', isLoading: false }))
    },

    // DATA FILES
    exportTaxonomy({ commit, rootState }, params) {
        return webRequestClient
            .get('/audience_admin/wam_custom_segments/export/json', {
                params: { product_account_id: toNumber(rootState.account.accountId), wam_custom_taxonomy_id: params.taxoId }
            })
            .then(taxonomyCsv => {
                if (!isEmpty(taxonomyCsv)) {
                    const blob = new Blob([taxonomyCsv], { type: 'text/csv' })
                    const link = documentLink()
                    link.href = windowUrl(blob)
                    link.download = `custom_segment_hierarchy${dayjs().format('YYYYMMDDHHmm')}.csv`
                    //link.click() doesn't work with FF
                    link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }))
                } else {
                    errorHandler('error while downloading your data', commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    importTaxonomy({ commit, state, rootState }, params) {
        const uri = '/audience_admin/wam_custom_segments/import_csv/json'
        const data = {
            filename: params.file.name,
            product_account_id: rootState.account.accountId,
            wam_custom_taxonomy_id: state.selectedTaxoId,
            my_file: params.file
        }

        const config = {}

        return webRequestClient
            .post(uri, data, config)
            .then(response => {
                if (!isEmpty(response)) {
                    const _response = response.root[0]
                    commit('setRetrievedItems', {
                        id: _response.id,
                        items: helpers.parseItems(_response.children)
                    })
                    commit('setSelectedTaxoId', { id: _response.id })

                    // Update counters
                    let counterCreate = 0
                    let counterUpdate = 0
                    let counterDelete = 0
                    for (const item of _response.children) {
                        // root cs
                        if (item.action === 'create') counterCreate += 1
                        if (item.action === 'update' || (item.action === null && item?.rule?.action != null)) counterUpdate += 1
                        if (item.action === 'delete') counterDelete += 1
                        // children cs
                        if (!isEmpty(item.children)) {
                            const childrenNodes = helpers.getNodeChildren(item.children, item.id)
                            for (const child of childrenNodes) {
                                if (child.action === 'create') counterCreate += 1
                                if (child.action === 'update' || (child.action === null && child?.rule?.action != null)) counterUpdate += 1
                                if (child.action === 'delete') counterDelete += 1
                            }
                        }
                    }

                    commit('setTaxoCounter', { type: 'created', count: counterCreate })
                    commit('setTaxoCounter', { type: 'updated', count: counterUpdate })
                    commit('setTaxoCounter', { type: 'deleted', count: counterDelete })

                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: `Your ${_response.taxonomy_type} taxonomy has been successfully uploaded`
                        },
                        { root: true }
                    )
                } else {
                    errorHandler('error while uploading your data', commit)
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    // RULE
    checkRuleValidation({ commit }, params) {
        return webRequestClient
            .get('/audience_admin/rules/check/json', { params })
            .then(response => {
                if (response) {
                    commit('setIsRuleValid', { isValid: true })
                } else {
                    commit('setIsRuleValid', { isValid: false })
                }
            })
            .catch(error => {
                const status = get(error, 'apiError.status', null)
                if (status === 406) {
                    commit('setIsRuleValid', { isValid: false })
                    errorHandler(error)
                } else {
                    errorHandler(error, commit)
                }
            })
    },

    async retrieveRuleEstimation({ commit, state, rootState }, params) {
        const account_id = get(rootState, 'account.accountId', '')
        const requestParams = {
            account_id,
            rule: params.rule,
            method: 'estimate',
            version: 1
        }

        try {
            commit('setIsLoading', { item: 'rule', isLoading: true })

            const requestKey = uuRequestKeys()
            commit('setLastUuRequestKey', { key: requestKey })
            const lastUuRequestKey = state.lastUuRequestKey

            let response = { status: 'NOT_READY' }

            while (response.status && response.status === 'NOT_READY' && requestKey === lastUuRequestKey) {
                response = await webRequestClient.post('/audience_admin/criteria/statistic.json', requestParams)

                if (response.status === 'KO') {
                    //no response or functionnal error
                    commit('setIsLoading', { item: 'rule', isLoading: false })
                    errorHandler('Unable to retrieve estimation', commit)
                } else {
                    //if response  === please wait
                    const isStillPending = get(response, 'status', '') === 'NOT_READY'

                    if (!isStillPending) {
                        //add data to store
                        commit('setRuleEstimation', { estimation: response.estimate, segmentId: params.segmentId })
                        //stop loader
                        commit('setIsLoading', { item: 'rule', isLoading: false })
                    }
                }
            }
        } catch (error) {
            commit('setIsLoading', { item: 'rule', isLoading: false })
            errorHandler(error, commit)
        }
    },

    tieSubItemToParent({ commit, state }, params) {
        commit('setParentId', params)
        commit('setAddItem', params)
        commit('setTaxoCounter', { type: 'dragged', count: state.counters.dragged + 1 })
    },

    // DATA UPDATES
    updateCustomSegment({ commit, state }, { action, item, canEditKey = false }) {
        if (!find(state.updatedSegmentIds, id => id === state.selectedSegmentId) && !find(state.createdSegmentKeys, key => key === state.selectedSegmentId)) {
            commit('setUpdatedSegmentIds', { segmentId: state.selectedSegmentId })
            commit('updateTaxoCounters', { item: 'updated', action: 'increase' })
        }
        commit('setCustomSegmentData', { action, item, canEditKey })
    },

    createCustomSegment({ commit }, params) {
        commit('setUpdateList', params)
        commit('updateTaxoCounters', { item: 'created', action: 'increase' })
        commit('setCreatedSegmentKeys', { key: params.key })
    },

    deleteCustomSegment({ commit, dispatch, state }, params) {
        //- decrease => undelete node and its children
        //- increase => delete node and its children
        const counterAction = params.node.action === 'delete' ? 'decrease' : 'increase'
        const isDeleting = counterAction === 'increase'

        const childrenCount = isEmpty(params.node.children) ? [0, 0, 0] : countChildren(params.node, isDeleting, state)

        //- undelete node and its children
        if (counterAction === 'decrease') {
            //- update node.children actions
            dispatch('updateCsActionsOnUndelete', { node: params.node })
            // update counters
            if (isNil(params.node.id) && includes(state.createdSegmentKeys, params.node.key)) {
                commit('updateTaxoCounters', { item: 'created', action: 'increase' })
            } else if (includes(state.updatedSegmentIds, params.node.id)) {
                commit('updateTaxoCounters', { item: 'updated', action: 'increase' })
            }
            commit('updateTaxoCounters', {
                item: 'deleted',
                action: counterAction,
                childrenCount
            })
        } else if (counterAction === 'increase') {
            //- delete node and its children
            commit('setCustomSegmentData', { action: 'delete', item: params.node })

            // update counters
            if (isNil(params.node.id) && includes(state.createdSegmentKeys, params.node.key)) {
                commit('updateTaxoCounters', { item: 'created', action: 'decrease' })
            } else if (includes(state.updatedSegmentIds, params.node.id)) {
                commit('updateTaxoCounters', { item: 'updated', action: 'decrease' })
            }
            commit('updateTaxoCounters', {
                item: 'deleted',
                action: counterAction,
                childrenCount
            })
        }
    },

    updateCsActionsOnUndelete({ commit, dispatch, state }, params) {
        const undeletedNode = params.node
        if (isNil(undeletedNode.id) && includes(state.createdSegmentKeys, undeletedNode.key)) {
            commit('setSegmentAction', { item: undeletedNode, action: 'create' })
        } else if (includes(state.updatedSegmentIds, undeletedNode.id)) {
            commit('setSegmentAction', { item: undeletedNode, action: 'edit' })
        } else {
            commit('setSegmentAction', { item: undeletedNode, action: null })
        }
        if (!isEmpty(undeletedNode.children)) {
            for (const child of undeletedNode.children) {
                dispatch('updateCsActionsOnUndelete', { node: child })
            }
        }
    },

    // TAXONOMY SAVE
    checkId({ commit, state, rootState }, params) {
        const cs_key = params.cs_id + '_' + params.cs_category

        if (find(state.createdSegmentKeys, key => key === cs_key)) {
            commit(
                'notifications/setAddedNotification',
                {
                    type: 'error',
                    message: 'Element already exists for this custom segment type'
                },
                { root: true }
            )
            return null
        }

        const route = '/audience_admin/wam_custom_segments/details/json'

        const _params = {
            params: {
                conditions: {
                    product_account_id: toNumber(rootState.account.accountId),
                    custom_identifier: params.cs_id,
                    custom_category: params.cs_category
                }
            }
        }

        return webRequestClient
            .get(route, _params)
            .then(response => {
                if (!isEmpty(response) && response.list) {
                    return size(response.list)
                }
                return 0
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    },

    saveTaxonomy({ commit, dispatch, state, rootState }, { displaySavedTaxo }) {
        commit('setIsProcessing', { value: true })
        const mapChildren = (child, callback) => {
            if (!child.children || child.children.length === 0) {
                return []
            }
            return child.children.map((_node) => {
                callback(_node.action === null)
                _node.children = mapChildren(_node, (_isSaved) => {
                    if (!_node.isSaved) {
                        _node.isSaved = _isSaved
                    }
                })
                return _node
            })
        }
        const children = cloneDeep(state.hierarchies[state.selectedTaxoId]).map((node) => {
            node.children = mapChildren(node, (_isSaved) => {
                if (!node.isSaved || node.isSaved === undefined) {
                    node.isSaved = _isSaved
                }
            })
            if (node.children.length === 0) {
                node.isSaved = node.action === null
            }
            return node
        })
        // const hasDelete = children.some((child) => child.action === 'delete')
        const params = {
            hierarchy: {
                root: [{ children, id: state.selectedTaxoId }]
            },
            product_account_id: toNumber(rootState.account.accountId)
        }

        return webRequestClient
            .put('/audience_admin/wam_custom_segments/bulk_update/json', params)
            .then(response => {
                if (!isEmpty(response)) {
                    const _response = response.root[0]
                    commit('resetTaxoCountingData', {})
                    if (displaySavedTaxo) {
                        commit('setRetrievedItems', {
                            id: _response.id,
                            items: helpers.parseItems(_response.children)
                        })
                        dispatch(
                            'data/customTaxonomies/retrieveItems',
                            {
                                taxoId: toNumber(state.selectedTaxoId),
                                silent: true
                            },
                            { root: true }
                        ).then(segments => {
                            commit(
                                'data/customTaxonomies/updateTaxonomyInCollection',
                                {
                                    taxoId: _response.id,
                                    frequencyOpts: rootState.data.settings.general.frequency,
                                    segments
                                },
                                {
                                    root: true
                                }
                            )
                        })
                    } else {
                        commit('setRetrievedItems', {
                            id: _response.id,
                            items: []
                        })
                    }
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Your taxonomy has been successfully updated'
                        },
                        { root: true }
                    )
                    commit('setIsProcessing', { value: false })
                    return { isSaved: true }
                } else {
                    errorHandler('error while updating your data', commit)
                    commit('setIsProcessing', { value: false })

                    return { isSaved: false }
                }
            })
            .catch(error => {
                commit('setIsLoading', { item: 'taxonomy', isLoading: false })
                commit('setIsProcessing', { value: false })
                const status = get(error, 'apiError.status', null)
                if (status === 400) {
                    const formatMessage = msg => {
                        try {
                            return JSON.parse(msg)
                        } catch {
                            return msg
                        }
                    }
                    const message = formatMessage(error.apiError.message)

                    if (typeof message === 'object') {
                        const errorMessages = []
                        for (const [key, value] of Object.entries(message)) {
                            const errorMessage = `${key} ${value.join(' and ')}`
                            errorMessages.push(errorMessage)
                        }
                        commit('notifications/setAddedNotification', { type: 'error', message: errorMessages.join(' AND ') }, { root: true })
                    } else {
                        commit('notifications/setAddedNotification', { type: 'error', message: message }, { root: true })
                    }
                } else {
                    errorHandler(error, commit)
                }
            })/*.finally(() => {
                if (hasDelete) {
                    window.location.reload()
                }
            })*/
    },

    resetStoreOnRouteLeave({ commit }) {
        commit('resetTaxoCountingData', {})
        commit('resetStoreOnLeave', {})
    },

    /**
     * Set the new status for sharing between 2 accounts
     * @param {Object} params
     * @param {Number} params.wam_custom_taxonomy_id id of the taxonomy
     * @param {Object} sharings payload of items to send
     * @param {String | Number} sharings.sharing_account_id if of the account to which share the taxonomy
     * @param {String} sharings.label label of the taxonomy to share with the account
     * @param {String} sharings.status active - inactive, to the new taxonomy status
     * @returns {Promise}
     */
    shareTaxonomy({ commit, dispatch, rootState }, params) {
        const _params = {
            ...params,
            account_id: toNumber(rootState.account.accountId)
        }
        return webRequestClient
            .post('/audience_admin/accounts_wam_custom_taxonomies/bulk/json', _params)
            .then(response => {
                if (!isEmpty(response) && get(response, 'status', '') !== 'KO') {
                    commit(
                        'notifications/setAddedNotification',
                        {
                            type: 'success',
                            message: 'Taxonomy successfully shared'
                        },
                        { root: true }
                    )
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
            .finally(() => {
                commit('setDisplaySharingTaxonomyModal', { display: false })
                commit('setSelectedSharingTaxonomy', { taxo: null })
                dispatch('retrieveTaxonomySharingStatus', {})
            })
    },

    /**
     * Retrieves the status for each taxonomy and set it to the store
     * @returns {Promise}
     */
    retrieveTaxonomySharingStatus({ commit, rootState }) {
        const _params = {
            params: {
                account_id: toNumber(rootState.account.accountId)
            }
        }
        return webRequestClient
            .get('/audience_admin/accounts_wam_custom_taxonomies.json', _params)
            .then(response => {
                if (!isEmpty(response)) {
                    commit('data/settings/setSharingTaxonomyStatus', { sharedTaxonomies: response.list }, { root: true })
                }
            })
            .catch(error => {
                errorHandler(error, commit)
            })
    }
}

export default actions
